import React, { useEffect } from 'react'
import { AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai'
import { Link, useNavigate } from 'react-router-dom'
import { FaEye } from 'react-icons/fa'
import Ratings from '../Ratings'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { add_to_card, messageClear, add_to_wishlist } from '../../store/reducers/cardReducer'
import { saveProductToSession, getGuestSessionId } from '../../utils/cartUtils'
import { getProxiedUrl } from '../../utils/imageUtils'
import { getDisplayPrice } from '../../utils/priceCalculator'

const ShopProducts = ({ products, sellerId, sellerName, styles }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo } = useSelector(state => state.auth)
  const { successMessage, errorMessage } = useSelector(state => state.card)

  const add_card = (p) => {
      if (userInfo) {
          dispatch(add_to_card({
              userId: userInfo.id,
              quantity: 1,
              productId: p._id,
              price: p.price,
              packSize: p.packSize,
              selectedImage: p.images?.[0] || "",
          }))
      } else {
          // For guest users, add to cart directly
          dispatch(add_to_card({
              userId: null,
              quantity: 1,
              productId: p._id,
              price: p.price,
              packSize: p.packSize,
              selectedImage: p.images?.[0] || "",
              sellerId: sellerId,
          }))
      }
  }
  
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage)
            dispatch(messageClear())
        }
        if (errorMessage) {
            toast.error(errorMessage)
            dispatch(messageClear())
        }
    }, [errorMessage, successMessage])

    const add_wishlist = (pro) => {
        if (userInfo) {
            dispatch(
              add_to_wishlist({
                userId: userInfo.id,
                productId: pro._id,
                name: pro.name,
                price: pro.price,
                image: pro.images[0],
                discount: pro.discount,
                rating: pro.rating,
                slug: pro.slug,
                currency: pro.currency,
              })
            );
        } else {
            // For guest users, add to wishlist using sessionId
            const sessionId = getGuestSessionId();
            
            dispatch(
              add_to_wishlist({
                sessionId: sessionId,
                productId: pro._id,
                name: pro.name,
                price: pro.price,
                image: pro.images[0],
                discount: pro.discount,
                rating: pro.rating,
                slug: pro.slug,
                currency: pro.currency,
              })
            );
        }
    }

    return (
      <div
        className={`w-full grid ${
          styles === "grid"
            ? "grid-cols-3 md-lg:grid-cols-2 md:grid-cols-2"
            : "grid-cols-1 md-lg:grid-cols-2 md:grid-cols-2"
        } gap-6`}
      >
        {products.map((p, i) => (
          <div
            key={i}
            className={`flex transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
              styles === "grid"
                ? "flex-col justify-start items-start"
                : "justify-start items-center md-lg:flex-col md-lg:justify-start md-lg:items-start"
            } w-full gap-4 bg-white p-4 rounded-xl border border-gray-100`}
          >
            <div
              className={
                styles === "grid"
                  ? "w-full relative group h-[200px] md:h-[220px] xs:h-[180px] overflow-hidden rounded-lg"
                  : "md-lg:w-full relative group h-[200px] md:h-[220px] overflow-hidden rounded-lg"
              }
            >
              <Link to={`/${sellerName}/product/details/${p.slug}`} className="block h-full">
                <img
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  src={getProxiedUrl(p.images[0])}
                  alt={p.name}
                />
              </Link>

              <div className="absolute top-4 right-4 flex flex-col gap-2">
                {p.discount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    -{p.discount}%
                  </span>
                )}
              </div>

              <ul className="flex transition-all duration-300 -bottom-10 justify-center items-center gap-3 absolute w-full group-hover:bottom-4">
                <li
                  onClick={() => add_wishlist(p)}
                  className="w-[42px] h-[42px] cursor-pointer bg-white/90 backdrop-blur-sm flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-500 shadow-lg"
                >
                  <AiFillHeart className="text-xl" />
                </li>
                <Link
                  to={`/${sellerName}/product/details/${p.slug}`}
                  className="w-[42px] h-[42px] cursor-pointer bg-white/90 backdrop-blur-sm flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-500 shadow-lg"
                >
                  <FaEye className="text-xl" />
                </Link>
                <li
                  onClick={() => add_card(p)}
                  className="w-[42px] h-[42px] cursor-pointer bg-white/90 backdrop-blur-sm flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-500 shadow-lg"
                >
                  <AiOutlineShoppingCart className="text-xl" />
                </li>
              </ul>
            </div>
            <div className="flex justify-start items-start flex-col gap-2 w-full">
              <h2 className="text-lg font-semibold text-gray-800 line-clamp-2 hover:text-indigo-600 transition-colors duration-200">
                {p.name}
              </h2>
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-col gap-1">
                  {(() => {
                    const display = getDisplayPrice(p);
                    return (
                      <>
                        <span className="text-xl font-bold text-indigo-600">
                          {display.currency}
                          {Number(display.price).toFixed(2)}
                          {display.isBulk && (
                            <span className="text-sm font-normal text-gray-600 ml-1">for {display.quantity}</span>
                          )}
                        </span>
                        {display.isBulk ? (
                          <span className="text-sm text-gray-500">
                            {display.currency}{Number(display.perItemPrice).toFixed(2)} per item
                          </span>
                        ) : p.discount > 0 && (
                          <span className="text-sm text-gray-500 line-through">
                            {p.currency}{Number(p.price).toFixed(2)}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-1">
                  <Ratings ratings={p.rating} />
                  <span className="text-sm text-gray-500">({p.rating})</span>
                </div>
              </div>
              {(p.showStockQuantity !== false || p.stock <= 0) && (
                <div className="flex items-center gap-2 mt-2">
                  <span className={`w-2 h-2 rounded-full ${p.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-sm text-gray-600">
                    {p.stock > 0 ? (p.showStockQuantity ? `${p.stock} in stock` : 'In stock') : 'Out of stock'}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-600 line-clamp-2 mt-1 whitespace-pre-line">
                {p.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
}

export default ShopProducts